(function() {
  'use strict';

  // Live references to the current header DOM. Shopify can replace a section's
  // markup at any time (theme editor, section rerender), so these are re-read
  // by init() instead of being captured once at first run.
  var drawer = null;
  var scrim = null;
  var burger = null;
  var searchOverlay = null;
  var searchInput = null;
  var searchOpeners = [];
  var searchLastFocus = null;

  // Binding is tracked per element, not per page. Re-running init() after a
  // rerender must bind the replaced nodes without giving a node that survived
  // a second copy of the same listener.
  function bindOnce(el, key, bind) {
    if (!el) return;
    var flag = '__ddBound_' + key;
    if (el[flag]) return;
    el[flag] = true;
    bind(el);
  }

  // The drawer and the search overlay both want to lock body scroll. Recompute
  // from both states so closing one does not unlock while the other is open.
  function syncBodyLock() {
    var drawerOpen = !!drawer && drawer.getAttribute('aria-hidden') === 'false';
    var searchOpen = !!searchOverlay && searchOverlay.getAttribute('aria-hidden') === 'false';
    document.body.style.overflow = (drawerOpen || searchOpen) ? 'hidden' : '';
  }

  function open() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    if (scrim) { scrim.removeAttribute('hidden'); scrim.classList.add('is-on'); }
    if (burger) burger.setAttribute('aria-expanded', 'true');
    syncBodyLock();
  }

  function close() {
    if (drawer) drawer.setAttribute('aria-hidden', 'true');
    if (scrim) { scrim.classList.remove('is-on'); scrim.setAttribute('hidden', ''); }
    if (burger) burger.setAttribute('aria-expanded', 'false');
    syncBodyLock();
  }

  function toggle() {
    if (!drawer) return;
    var isOpen = drawer.getAttribute('aria-hidden') === 'false';
    if (isOpen) close(); else open();
  }

  // The drawer's early return is scoped to the drawer's own setup, so a page
  // rendered without drawer markup still gets a working search overlay.
  function initDrawer() {
    drawer = document.getElementById('dd-drawer');
    scrim = document.querySelector('[data-dd-scrim]');
    burger = document.querySelector('.dd-burger[data-dd-drawer-toggle]');

    var toggles = document.querySelectorAll('[data-dd-drawer-toggle]');
    if (!drawer || !toggles.length) return;

    toggles.forEach(function(node) {
      bindOnce(node, 'drawerToggle', function(btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          toggle();
        });
      });
    });

    bindOnce(scrim, 'scrim', function(el) {
      el.addEventListener('click', close);
    });
  }

  // Only the opener owns the overlay's expanded state. The close button inside
  // the dialog shares [data-dd-search-toggle] but has no aria-controls, and
  // aria-expanded on it would announce a control it does not own.
  function setSearchExpanded(expanded) {
    searchOpeners.forEach(function(btn) {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function searchFocusables() {
    if (!searchOverlay) return [];
    return Array.prototype.filter.call(
      searchOverlay.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function(el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0; }
    );
  }

  function openSearch() {
    if (!searchOverlay) return;
    if (searchOverlay.getAttribute('aria-hidden') === 'false') return;
    close();
    searchLastFocus = document.activeElement;
    searchOverlay.setAttribute('aria-hidden', 'false');
    setSearchExpanded(true);
    syncBodyLock();
    // Capture the input this open meant to focus. A rerender inside the delay
    // can repoint or null the module reference, so focus the captured node and
    // only while it is still in the document.
    if (searchInput) {
      var pendingInput = searchInput;
      setTimeout(function() {
        if (document.contains(pendingInput)) pendingInput.focus();
      }, 50);
    }
  }

  function closeSearch() {
    if (!searchOverlay) return;
    var wasOpen = searchOverlay.getAttribute('aria-hidden') === 'false';
    searchOverlay.setAttribute('aria-hidden', 'true');
    setSearchExpanded(false);
    syncBodyLock();
    // The opener can be detached by a rerender while the overlay is open.
    // Focusing a detached node silently drops focus to <body>, so restore only
    // to an element still connected to the current document.
    if (wasOpen && searchLastFocus && typeof searchLastFocus.focus === 'function'
        && document.contains(searchLastFocus)) {
      searchLastFocus.focus();
    }
    searchLastFocus = null;
  }

  // Search overlay. Set up independently of the drawer and re-entrant, so it
  // works on a page with no drawer markup and after a section rerender.
  function initSearch() {
    searchOverlay = document.getElementById('dd-search-overlay');
    searchInput = searchOverlay ? searchOverlay.querySelector('.dd-search-overlay__input') : null;
    searchOpeners = document.querySelectorAll('[data-dd-search-toggle][aria-controls]');

    // A rerender can detach the element that opened the overlay. Drop the stale
    // reference here rather than carrying a detached node to the next close.
    if (searchLastFocus && !document.contains(searchLastFocus)) searchLastFocus = null;

    document.querySelectorAll('[data-dd-search-toggle]').forEach(function(node) {
      bindOnce(node, 'searchToggle', function(btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          var isOpen = searchOverlay && searchOverlay.getAttribute('aria-hidden') === 'false';
          if (isOpen) { closeSearch(); } else { openSearch(); }
        });
      });
    });

    // Clicking the backdrop (the overlay itself, not the panel) dismisses.
    bindOnce(searchOverlay, 'searchBackdrop', function(el) {
      el.addEventListener('click', function(e) {
        if (e.target === searchOverlay) closeSearch();
      });
    });
  }

  // Guard at the submit event so button, keyboard and programmatic paths share
  // one validation check. Avoid the native submit method, which would bypass
  // this handler.
  function initSearchForms() {
    document.querySelectorAll('.dd-search-overlay__form, .dd-drawer__search').forEach(function(node) {
      bindOnce(node, 'searchForm', function(form) {
        var input = form.querySelector('input[name="q"]');
        var submitButton = form.querySelector('button[type="submit"]');

        form.addEventListener('submit', function(e) {
          if (input && !input.value.trim()) {
            e.preventDefault();
            input.focus();
          }
        });

        if (input) {
          input.addEventListener('keydown', function(e) {
            if (e.isComposing || e.key !== 'Enter') return;
            e.preventDefault();
            if (!input.value.trim()) {
              input.focus();
              return;
            }
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitButton || undefined);
            } else if (submitButton) {
              submitButton.click();
            }
          });
        }
      });
    });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close(); closeSearch(); }

    if (e.key !== 'Tab') return;
    if (!searchOverlay || searchOverlay.getAttribute('aria-hidden') !== 'false') return;

    var focusable = searchFocusables();
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // These listen on the document, not on header nodes, so they are bound once
  // per page even if a rerender evaluates this script again. The section events
  // re-run init() against the replaced markup; bindOnce() keeps nodes that
  // survived from picking up a second listener.
  function bindGlobalListeners() {
    if (window.__ddHeaderGlobalBound) return;
    window.__ddHeaderGlobalBound = true;

    document.addEventListener('keydown', onKeydown);
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:unload', init);
  }

  function init() {
    initDrawer();
    initSearch();
    initSearchForms();
    bindGlobalListeners();
    // Markup may have been swapped out while a dialog was open, so recompute
    // the lock from whatever is actually on the page now.
    syncBodyLock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
